import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Newspaper,
  LogOut,
  Menu,
  X,
  Compass,
  PenSquare,
  ShieldAlert,
  ScanSearch,
  Calculator,
  Kanban,
  CalendarDays,
  Sparkles,
  Wrench,
  FileSignature,
  Bot,
  BrainCircuit,
  Radio,
  Library,
  LineChart,
} from 'lucide-react';
import { useState } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useAuth } from '@/hooks/useAuth';
import { useViewModeStore } from '@/stores/view-mode';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface NavItemDef {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  disabled?: boolean;
}

const navItemsConsultor: NavItemDef[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/empresas', label: 'Empresas', icon: Building2 },
  { path: '/licitacoes', label: 'Licitações', icon: FileText },
  { path: '/mercado-inteligencia', label: 'Inteligência de Mercado', icon: LineChart },
  { path: '/gestao-licitacoes', label: 'Gestão da Licitação', icon: Kanban },
  { path: '/calendario', label: 'Calendário', icon: CalendarDays },
  { path: '/noticias', label: 'Notícias', icon: Newspaper },
  { path: '/agentes-ia/oraculo-edital', label: 'Oráculo do Edital', icon: Compass },
  { path: '/agentes-ia/redator-ninja', label: 'Redator Ninja', icon: PenSquare },
  { path: '/agentes-ia/guardiao-disputa', label: 'Guardião da Disputa', icon: ShieldAlert },
  { path: '/agentes-ia/auditor-360', label: 'Auditor 360°', icon: ScanSearch },
  { path: '/agentes-ia/central-planilhas', label: 'Central de Planilhas', icon: Calculator },
];

/**
 * Preview of the future empresa (tenant) navigation, used from a staff
 * account via the Visão Empresa toggle until dedicated empresa logins exist.
 * "Minha Empresa" and "Meu Feed" resolve to the empresa selected in
 * ViewModeSwitcher (`previewEmpresaId`); when none is selected they are
 * disabled rather than pointing anywhere real.
 */
function buildNavItemsEmpresa(previewEmpresaId: number | null): NavItemDef[] {
  const hasEmpresa = previewEmpresaId !== null;
  return [
    {
      path: hasEmpresa ? `/empresas/${previewEmpresaId}/feed` : '#',
      label: 'Meu Feed',
      icon: Sparkles,
      disabled: !hasEmpresa,
    },
    { path: hasEmpresa ? `/empresas/${previewEmpresaId}/inteligencia` : '#', label: 'Inteligência', icon: BrainCircuit, disabled: !hasEmpresa },
    { path: '/mercado-inteligencia', label: 'Inteligência de Mercado', icon: LineChart },
    { path: '/licitacoes', label: 'Licitações', icon: FileText },
    { path: '/noticias', label: 'Notícias', icon: Newspaper },
    { path: hasEmpresa ? `/gestao-licitacoes?empresa=${previewEmpresaId}` : '/gestao-licitacoes', label: 'Gestão da Licitação', icon: Kanban },
    { path: hasEmpresa ? `/andamento-processos?empresa=${previewEmpresaId}` : '#', label: 'Andamento dos Processos', icon: Radio, disabled: !hasEmpresa },
    { path: hasEmpresa ? `/calendario?empresa=${previewEmpresaId}` : '/calendario', label: 'Calendário', icon: CalendarDays },
    { path: hasEmpresa ? `/empresas/${previewEmpresaId}/contratos` : '#', label: 'Gestão de contratos', icon: FileSignature, disabled: !hasEmpresa },
    { path: hasEmpresa ? `/empresas/${previewEmpresaId}/documentos` : '#', label: 'Central de Documentos', icon: Library, disabled: !hasEmpresa },
    { path: hasEmpresa ? `/empresas/${previewEmpresaId}/robo-lances` : '#', label: 'Robô de lances', icon: Bot, disabled: !hasEmpresa },
    { path: '/agentes-ia/ferramentas', label: 'Ferramentas', icon: Wrench },
    {
      path: hasEmpresa ? `/empresas/${previewEmpresaId}/perfil-tecnico` : '#',
      label: 'Minha Empresa',
      icon: Building2,
      disabled: !hasEmpresa,
    },
  ];
}

function Sidebar() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { logout, user } = useAuth();
  const { mode, previewEmpresaId } = useViewModeStore();

  const navItems =
    mode === 'empresa' ? buildNavItemsEmpresa(previewEmpresaId) : navItemsConsultor;

  const NavItem = ({ path, label, icon: Icon, disabled }: NavItemDef) => {
    if (disabled) {
      return (
        <span
          title="Selecione uma empresa no seletor de visualização para habilitar"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-ds-text-secondary opacity-50 cursor-not-allowed"
        >
          <Icon className="h-5 w-5" />
          {label}
        </span>
      );
    }

    return (
      <NavLink
        to={path}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
            isActive
              ? 'bg-ds-brand-subtle/50 text-ds-brand'
              : 'text-ds-text-secondary hover:bg-ds-surface2 hover:text-ds-text-primary'
          )
        }
        onClick={() => setIsMobileOpen(false)}
      >
        <Icon className="h-5 w-5" />
        {label}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-ds-surface text-ds-text-primary border border-ds-border"
      >
        {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-40 w-64 bg-ds-surface border-r border-ds-border',
          'transform transition-transform duration-300 ease-in-out lg:transform-none',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-ds-border">
            <div className="w-8 h-8 rounded-lg bg-ds-brand-solid flex items-center justify-center" aria-hidden="true">
              <span className="text-white font-bold text-xs tracking-tighter">LO</span>
            </div>
            <div>
              <h1 className="font-semibold text-ds-text-primary">LicitaOS</h1>
              <p className="text-xs text-ds-text-secondary">Painel Admin</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map((item) => (
              <NavItem key={item.label} {...item} />
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-ds-border">
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-ds-surface2 mb-3">
              <div className="w-8 h-8 rounded-full bg-ds-info/20 flex items-center justify-center">
                <span className="text-ds-info font-medium text-sm">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ds-text-primary truncate">
                  {user?.name || 'Usuário'}
                </p>
                <p className="text-xs text-ds-text-secondary truncate">
                  {user?.email || ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                void logout();
              }}
              className="flex w-full items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-ds-text-secondary hover:bg-ds-danger/10 hover:text-ds-danger transition-all duration-200"
            >
              <LogOut className="h-5 w-5" />
              Sair
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}

export { Sidebar };
